<template>
  <div class="d-flex flex-column justify-content-end w-100">
    <label
      v-if="itemModel.vars.label"
      :id="labelId"
      :for="itemModel.vars.id"
      :class="
        'form-label' +
        (itemModel.vars.label_attr && itemModel.vars.label_attr.class ? ' ' + itemModel.vars.label_attr.class : '') +
        (itemModel.vars.required ? ' required' : '')
      "
    >
      {{ itemModel.vars.label }}{{ itemModel.vars.required ? "*" : "" }}
      <span
        v-if="itemModel.vars.help && !isMobile()"
        v-tooltip="{ title: itemModel.vars.help, textWrap: true }"
        class="form-info ms-1"
      >
        <FontAwesomeIcon icon="fa-light fa-circle-question" />
      </span>
    </label>
    <div v-if="isMobile() && itemModel.vars.help" class="form-info">
      <FontAwesomeIcon icon="fa-light fa-circle-question" />
      {{ itemModel.vars.help }}
    </div>
    <div class="icon-picker__value">
      <a
        v-tooltip="{ title: iconLibraryLabel }"
        :href="iconLibraryUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="icon-picker__preview"
        :aria-label="iconLibraryLabel"
      >
        <FontAwesomeIcon :icon="previewIcon ?? 'fa-light fa-magnifying-glass'" />
      </a>
      <input
        :id="itemModel.vars.id"
        type="text"
        class="form-control"
        :class="{ 'is-invalid': isInvalid }"
        :value="inputValue"
        :disabled="itemModel.vars.disabled === true"
        :placeholder="$t('empty_icon')"
        :aria-label="itemModel.vars.label ? null : $t('icon.selected')"
        :aria-invalid="isInvalid ? 'true' : null"
        :aria-describedby="itemModel.vars.error ? errorId : null"
        @focus="focus"
        @input="onInput($event.target.value)"
        @blur="onBlur"
      />
    </div>
    <div
      v-if="iconChoices?.length > 0"
      class="icon-picker"
      :class="{ '-invalid': isInvalid }"
      :role="isMultiple ? 'group' : 'radiogroup'"
      :aria-labelledby="itemModel.vars.label ? labelId : null"
      :aria-label="itemModel.vars.label ? null : $t('icon.list')"
      :aria-describedby="itemModel.vars.error ? errorId : null"
    >
      <button
        v-for="(choice, index) in iconChoices"
        :key="choice.value"
        :ref="(el) => setItemRef(el, index)"
        v-tooltip="{ title: choice.label }"
        type="button"
        class="icon-picker__item"
        :class="{ '-selected': isSelected(choice.value) }"
        :role="isMultiple ? null : 'radio'"
        :disabled="itemModel.vars.disabled === true"
        :aria-pressed="isMultiple ? isSelected(choice.value) : null"
        :aria-checked="isMultiple ? null : isSelected(choice.value)"
        :tabindex="isMultiple || isFocusable(index) ? null : -1"
        :aria-label="choice.label"
        @focus="focus"
        @click="toggle(choice.value)"
        @keydown.left.prevent="focusSibling(index, -1)"
        @keydown.up.prevent="focusSibling(index, -1)"
        @keydown.right.prevent="focusSibling(index, 1)"
        @keydown.down.prevent="focusSibling(index, 1)"
      >
        <FontAwesomeIcon :icon="choice.icon" />
      </button>
    </div>
    <div v-if="itemModel.vars.error" :id="errorId" class="invalid-feedback d-block">
      {{ itemModel.vars.error }}
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { formManager } from "@/services/form/form-manager";
import { formValidator } from "@/services/form/form-validator";
import { iconManager } from "@/services/utilities/icon-manager";

export const ICON_LIBRARY_URL = "https://fontawesome.com/search?f=classic&s=light";

export default {
  name: "IconPickerComponent",
  components: {
    FontAwesomeIcon,
  },
  props: {
    model: {
      type: Object,
      required: true,
    },
  },
  emits: ["update", "focus", "draft"],
  data() {
    return {
      itemModel: this.model,
      selected: this.normalize(this.parseValue(this.model?.vars?.value)),
      // Texte brut affiché : découplé de `selected` pour ne pas réécrire la saisie en cours
      // (ex. "fa-" tapé sans suite serait sinon effacé par le parsing à chaque frappe)
      inputValue: this.displayValue(this.normalize(this.parseValue(this.model?.vars?.value))),
      commitInputDebounced: null,
      debounceTimer: null,
    };
  },
  computed: {
    isMultiple() {
      return this.itemModel.vars.multiple === true;
    },

    labelId() {
      return this.itemModel.vars.id + "_label";
    },

    errorId() {
      return this.itemModel.vars.id + "_error";
    },

    iconLibraryUrl() {
      return ICON_LIBRARY_URL;
    },

    iconLibraryLabel() {
      return `${this.$t("icon.library")} ${this.$t("global.new_window")}`;
    },

    iconChoices() {
      const choices = this.itemModel?.vars?.attr?.["icons"];
      if (!choices) {
        return [];
      }
      return Object.values(choices)
        .map((choice) => this.buildChoice(choice))
        .filter((choice) => choice !== null);
    },

    isInvalid() {
      return Boolean(this.itemModel.vars.error || this.itemModel.vars.isInvalid);
    },

    previewIcon() {
      const value = this.isMultiple ? this.selected[0] : this.selected;
      if (!value) {
        return null;
      }
      const choice = this.iconChoices.find((item) => item.value === value);
      if (choice) {
        return choice.icon;
      }
      return formManager.isValidIconName(iconManager.parseIconName(value)) ? this.toIconClass(value) : null;
    },

    selectedIndex() {
      return this.iconChoices.findIndex((choice) => this.isSelected(choice.value));
    },
  },
  watch: {
    model() {
      this.itemModel = this.model;
      this.selected = this.normalize(this.parseValue(this.model?.vars?.value));
      this.inputValue = this.displayValue(this.selected);
      this.syncModelValue();
    },
  },
  created() {
    this.itemRefs = [];
    this.syncModelValue();
    // Laisse l'utilisateur finir de taper (ex. "fa-light fa-volume") avant de parser : à chaud,
    // une frappe intermédiaire comme "fa-light fa-v" ne correspond à aucun style connu et donnerait
    // un résultat tronqué si on parsait à chaque caractère
    this.commitInputDebounced = this.debounce(this.commitInput, 300);
  },
  beforeUnmount() {
    clearTimeout(this.debounceTimer);
  },
  methods: {
    // Une valeur stockée avec préfixe ("fa-light fa-check") échouerait à la validation : le nom
    // nu est réécrit dans le modèle, sans émettre (aucune action utilisateur)
    syncModelValue() {
      if (this.model?.vars?.value) {
        this.itemModel.vars.value = Array.isArray(this.selected) ? [...this.selected] : this.selected;
      }
    },

    parseValue(value) {
      if (Array.isArray(value)) {
        return value.map((item) => iconManager.parseIconName(item)).filter(Boolean);
      }
      return value ? iconManager.parseIconName(value) : value;
    },

    setItemRef(el, index) {
      this.itemRefs[index] = el;
    },

    buildChoice(choice) {
      const value = choice.data !== undefined && choice.data !== null ? choice.data : choice.value;
      if (!value) {
        return null;
      }
      const iconName = choice.attr && choice.attr["data-icon"] ? choice.attr["data-icon"] : value;
      return {
        value: String(value),
        icon: this.toIconClass(iconName),
        label: choice.label ? choice.label : String(value),
      };
    },

    toIconClass(iconName) {
      const name = String(iconName);
      return name.includes("fa-") ? name : "fa-light fa-" + name;
    },

    displayValue(value) {
      if (this.model?.vars?.multiple === true) {
        return Array.isArray(value) ? value.join(", ") : "";
      }
      return value ? String(value) : "";
    },

    normalize(value) {
      if (this.model?.vars?.multiple === true) {
        if (Array.isArray(value)) {
          return value.map((item) => String(item));
        }
        return value ? [String(value)] : [];
      }
      return value ? String(value) : null;
    },

    isSelected(value) {
      if (this.isMultiple) {
        return this.selected.includes(value);
      }
      return this.selected === value;
    },

    toggle(value) {
      if (this.isMultiple) {
        if (!this.isSelected(value)) {
          this.selected = [...this.selected, value];
        } else if (this.itemModel.vars.required === true && this.selected.length === 1) {
          // Champ obligatoire : au moins une icône doit rester sélectionnée
          return;
        } else {
          this.selected = this.selected.filter((item) => item !== value);
        }
      } else if (this.isSelected(value)) {
        // Désélection interdite sur un champ obligatoire : une valeur doit rester choisie
        if (this.itemModel.vars.required === true) {
          return;
        }
        this.selected = null;
      } else {
        this.selected = value;
      }
      this.inputValue = this.displayValue(this.selected);
      this.apply();
    },

    // Navigation clavier du radiogroup : la flèche déplace le focus et sélectionne (motif ARIA radio)
    focusSibling(index, step) {
      const total = this.iconChoices.length;
      if (!total || this.itemModel.vars.disabled === true) {
        return;
      }
      const nextIndex = (index + step + total) % total;
      const nextItem = this.itemRefs[nextIndex];
      if (nextItem) {
        nextItem.focus();
      }
      if (!this.isMultiple) {
        this.toggle(this.iconChoices[nextIndex].value);
      }
    },

    isFocusable(index) {
      return this.selectedIndex === -1 ? index === 0 : index === this.selectedIndex;
    },

    apply() {
      this.itemModel.vars.value = this.selected;
      formValidator.validModel(this.itemModel);
      this.$emit("update", this.selected);
      this.$emit("draft", this.itemModel);
    },

    focus() {
      this.$emit("focus");
    },

    parseInput(value) {
      const parsed = this.isMultiple
        ? value
            .split(",")
            .map((item) => iconManager.parseIconName(item))
            .filter(Boolean)
        : iconManager.parseIconName(value);
      return this.normalize(parsed);
    },

    commitInput(value) {
      this.selected = this.parseInput(value);
      this.apply();
    },

    onInput(value) {
      // Saisie affichée telle quelle (ne pas réécrire pendant la frappe, ex. "fa-" en cours de
      // saisie) ; le parsing/l'envoi de la valeur nue est débouncé, cf. created()
      this.inputValue = value;
      this.commitInputDebounced(value);
    },

    onBlur() {
      clearTimeout(this.debounceTimer);
      if (JSON.stringify(this.parseInput(this.inputValue)) !== JSON.stringify(this.selected)) {
        this.commitInput(this.inputValue);
      }
      this.inputValue = this.displayValue(this.selected);
    },

    debounce(fn, delay = 300) {
      return (...args) => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => fn.apply(this, args), delay);
      };
    },

    isMobile() {
      return window.innerWidth < 992;
    },
  },
};
</script>

<style lang="scss" scoped>
.icon-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;

  &.-invalid .icon-picker__item {
    border-color: $red;
  }
}

.icon-picker__value {
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-picker__preview {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid $primary;
  border-radius: $radius;
  background: $primary;
  font-size: 22px;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid $primary;
    outline-offset: 2px;
    box-shadow: inset 0 0 0 2px $white;

    @include dark-theme {
      outline-color: $white;
    }
  }
}

// Les règles des modales (.modal-content .modal-body svg) ciblent le svg directement et
// l'emportent sur la couleur héritée du lien
.icon-picker__value .icon-picker__preview svg {
  color: $white;

  @include dark-theme {
    color: $dark-text;
  }
}

.icon-picker__item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid $grey;
  border-radius: $radius;
  background: transparent;
  color: inherit;
  font-size: 20px;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--primary-color);
  }

  &.-selected {
    border-color: var(--primary-color);
    background: var(--primary-color);
    color: $white;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  @include dark-theme {
    border-color: $m-color_12;

    &:hover:not(:disabled),
    &.-selected {
      border-color: $blue;
    }
  }
}
</style>
